import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useVideo } from '../contexts/VideoContext';
import { AppBar, Toolbar, Button, Box, IconButton, Skeleton } from '@mui/material';
import { Menu as MenuIcon, Close as CloseIcon } from '@mui/icons-material';
import LanguageToggler from './LanguageToggler';

const Navigation: React.FC = () => {
  const { t, isLoading } = useLanguage();
  const { navbarLogoVisible } = useVideo();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isInHero, setIsInHero] = useState(true);
  const [isScrolled, setIsScrolled] = useState(true);
  const location = useLocation();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };


  const isActive = (path: string) => {
    return location.pathname === path;
  };

  // Scroll detection for dynamic background effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      // Show background when user scrolls down even slightly (more than 1px)
      setIsScrolled(scrollPosition <= 0.01);
      // When the user is out of the hero section
      setIsInHero(scrollPosition < 725);
    };

    window.addEventListener('scroll', handleScroll);
    // Check initial state
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '100%',
        maxWidth: '95%',
        zIndex: 1000,
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: isScrolled ? 'none' : '0 8px 32px rgba(0, 0, 0, 0.15)',
        transition: 'all 0.3s ease-in-out',
        backdropFilter: isScrolled ? 'blur(0px)' : 'blur(20px)',
        background: isInHero ? 'transparent' : 'rgba(36, 68, 115, 0.5)',
        border: isScrolled ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
        mt: 2,
      }}
    >
      <AppBar 
        position="static"
        elevation={0}
        sx={{ 
          backgroundColor: 'transparent',
          color: 'white',
          boxShadow: 'none',
        }}
      >
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        {/* Desktop Navigation */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2 }}>
          {isLoading ? (
            <>
              <Skeleton variant="text" width={60} height={40} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
              <Skeleton variant="text" width={60} height={40} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
              <Skeleton variant="text" width={80} height={40} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
            </>
          ) : (
            <>
              <Button
                component={Link}
                to="/"
                sx={{ 
                  fontWeight: isActive('/') ? 'bold' : 'normal',
                  textTransform: 'none',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: isScrolled ? 'rgba(36, 68, 115, 0.5)' : 'rgba(255, 255, 255, 0.2)' 
                  }
                }}
              >
                {t('nav.home')}
              </Button>
              <Button
                component={Link}
                to="/services"
                sx={{ 
                  fontWeight: isActive('/services') ? 'bold' : 'normal',
                  textTransform: 'none',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: isScrolled ? 'rgba(36, 68, 115, 0.5)' : 'rgba(255, 255, 255, 0.2)' 
                  }
                }}
              >
                {t('nav.services')}
              </Button>
              <Button
                component={Link}
                to="/contact"
                sx={{ 
                  fontWeight: isActive('/contact') ? 'bold' : 'normal',
                  textTransform: 'none',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: isScrolled ? 'rgba(36, 68, 115, 0.5)' : 'rgba(255, 255, 255, 0.2)' 
                  }
                }}
              >
                {t('nav.contact')}
              </Button>
            </>
          )}
        </Box>

        {/* Logo */}
        <Box 
          component={Link} 
          to="/" 
          className='md:w-[15%] md:me-14'
          sx={{
            opacity: location.pathname === '/' ? (navbarLogoVisible ? 1 : 0) : 1,
            transition: 'opacity 0.5s ease-in-out'
          }}
        >
          <img src="/images/logo.png" alt="Moktamel" width={90} height={90} />
        </Box>

        {/* Language Switcher */}
        <Box>
          <LanguageToggler sx={{ display: { xs: 'none', md: 'flex' } }} />
          {/* Mobile menu button */}
          <IconButton
            onClick={toggleMenu}
            sx={{ 
              display: { xs: 'block', md: 'none' },
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)'
              }
            }}
          >
            {isMenuOpen ? <CloseIcon /> : <MenuIcon />}
          </IconButton>
        </Box>
      </Toolbar>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <Box sx={{ 
          display: { xs: 'block', md: 'none' }, 
          p: 2, 
          backgroundColor: isInHero ? 'rgba(255, 255, 255, 0.4)' : 'rgba(36, 68, 115, 0.5)',
          backdropFilter: 'blur(10px)'
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {isLoading ? (
              <>
                <Skeleton variant="text" width="100%" height={40} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                <Skeleton variant="text" width="100%" height={40} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                <Skeleton variant="text" width="100%" height={40} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
              </>
            ) : (
              <>
                <Button
                  component={Link}
                  to="/"
                  fullWidth
                  onClick={closeMenu}
                  sx={{ 
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    fontWeight: isActive('/') ? 'bold' : 'normal',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.2)'
                    }
                  }}
                >
                  {t('nav.home')}
                </Button>
                <Button
                  component={Link}
                  to="/services"
                  fullWidth
                  onClick={closeMenu}
                  sx={{ 
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    fontWeight: isActive('/services') ? 'bold' : 'normal',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.2)'
                    }
                  }}
                >
                  {t('nav.services')}
                </Button>
                <Button
                  component={Link}
                  to="/contact"
                  fullWidth
                  onClick={closeMenu}
                  sx={{ 
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    fontWeight: isActive('/contact') ? 'bold' : 'normal',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.2)'
                    }
                  }}
                >
                  {t('nav.contact')}
                </Button>
              </>
            )}
            
            
            <LanguageToggler sx={{ display: { xs: 'block', md: 'none' } }} />
          </Box>
        </Box>
      )}
      </AppBar>
    </Box>
  );
};

export default Navigation;
